/**
 * Handoff holds keep "a Remix is in progress" true after a flow hands its generation to Rovo Chat.
 *
 * At a handoff the flow's machine is torn down, but Rovo Chat is still generating the result. A
 * hold, keyed by the backing conversation, stands in for the flow until that conversation settles,
 * so other entry points stay disabled and the single-active guard keeps refusing new starts.
 *
 * Holds count toward `isAnyRemixActive` and `isAnyRemixingNow`, and notify the same aggregate
 * subscribers as machines. `RemixWatchers` observes each held conversation and releases the hold.
 * Two timers make sure a hold can't disable Remix for good: a start grace for a conversation that
 * never starts generating (it finished before the handoff), and a maximum duration. Every release
 * carries a reason, so the creating surface can report how its holds end.
 */
import { useSyncExternalStore } from 'react';

import { notifyAnyMachineSubscribers, subscribeToAnyMachine } from './machineBridge';

/** The surface that created a hold, so it can release its own holds (e.g. on navigation). */
export type RemixHandoffHoldSource = 'object-sidebar';

/**
 * Why a hold ended. The first three come from the held conversation, the two timers are fallbacks,
 * and the rest are the creating surface releasing its own holds.
 */
export type RemixHandoffHoldReleaseReason =
	| 'settled'
	| 'switched-away'
	| 'agent-deactivated'
	| 'start-grace'
	| 'max-duration'
	| 'already-finished'
	| 'panel-closed'
	| 'left-page';

/** Longest a hold lasts, so a stream that never settles can't keep Remix disabled. */
export const REMIX_HANDOFF_HOLD_MAX_MS: number = 5 * 60 * 1000;

/**
 * How long a hold waits to see its conversation generating. If it never turns busy, the generation
 * finished before the handoff and the hold is released. Use this when a result may already exist.
 */
export const REMIX_HANDOFF_HOLD_START_GRACE_MS: number = 10 * 1000;

/**
 * The start grace for a handoff made right after the prompt was sent. A reused conversation only
 * turns busy after the send resolves and its context is fetched, which can be slow, so wait longer.
 */
export const REMIX_HANDOFF_HOLD_SEND_START_GRACE_MS: number = 30 * 1000;

/**
 * Fallback delay before releasing a stopped turn whose outcome is unclear.
 */
export const REMIX_HANDOFF_HOLD_SETTLE_MS: number = 2 * 1000;

interface RemixHandoffHold {
	holdId: number;
	source: RemixHandoffHoldSource;
	onRelease?: (reason: RemixHandoffHoldReleaseReason) => void;
	maxDurationTimer: ReturnType<typeof setTimeout>;
	startGraceTimer?: ReturnType<typeof setTimeout>;
}

const holds = new Map<string, RemixHandoffHold>();
let nextHoldId = 1;

const clearHoldTimers = (hold: RemixHandoffHold): void => {
	clearTimeout(hold.maxDurationTimer);
	if (hold.startGraceTimer !== undefined) {
		clearTimeout(hold.startGraceTimer);
	}
};

/** Release the hold for a conversation. No-op if there is none. */
export const releaseRemixHandoffHold = (
	conversationId: string,
	reason: RemixHandoffHoldReleaseReason,
	expectedHoldId?: number,
): void => {
	const hold = holds.get(conversationId);
	if (!hold || (expectedHoldId !== undefined && hold.holdId !== expectedHoldId)) {
		return;
	}
	clearHoldTimers(hold);
	holds.delete(conversationId);
	notifyAnyMachineSubscribers();
	try {
		hold.onRelease?.(reason);
	} catch {
		// Reporting a release must never affect it.
	}
};

/** Release every hold a surface created. */
export const releaseRemixHandoffHolds = (
	source: RemixHandoffHoldSource,
	reason: RemixHandoffHoldReleaseReason,
): void => {
	for (const [conversationId, hold] of Array.from(holds)) {
		if (hold.source === source) {
			releaseRemixHandoffHold(conversationId, reason);
		}
	}
};

/**
 * Hold "a Remix is in progress" until the conversation's handed-off generation settles. Call it
 * before ending the flow, so aggregate readers never see a gap between the two.
 */
export const holdRemixHandoff = ({
	conversationId,
	source,
	startGraceMs = REMIX_HANDOFF_HOLD_START_GRACE_MS,
	hasStartedGenerating = false,
	onRelease,
}: {
	conversationId: string;
	source: RemixHandoffHoldSource;
	/** Defaults to `REMIX_HANDOFF_HOLD_START_GRACE_MS`. */
	startGraceMs?: number;
	/**
	 * The conversation already generated a result, so the hold needs no start grace and releases
	 * as soon as the conversation is idle with a terminal outcome.
	 */
	hasStartedGenerating?: boolean;
	/** Called once, with the reason, when the hold is released. */
	onRelease?: (reason: RemixHandoffHoldReleaseReason) => void;
}): number | undefined => {
	if (!conversationId) {
		return undefined;
	}
	const holdId = nextHoldId++;
	const existing = holds.get(conversationId);
	if (existing) {
		clearHoldTimers(existing);
	}
	holds.set(conversationId, {
		holdId,
		source,
		onRelease,
		maxDurationTimer: setTimeout(
			() => releaseRemixHandoffHold(conversationId, 'max-duration', holdId),
			REMIX_HANDOFF_HOLD_MAX_MS,
		),
		startGraceTimer: hasStartedGenerating
			? undefined
			: setTimeout(
					() => releaseRemixHandoffHold(conversationId, 'start-grace', holdId),
					startGraceMs,
				),
	});
	notifyAnyMachineSubscribers();
	return holdId;
};

/** The held conversation has started generating, so it no longer needs the start grace. */
export const markRemixHandoffHoldGenerating = (
	conversationId: string,
	expectedHoldId?: number,
): void => {
	const hold = holds.get(conversationId);
	if (
		!hold ||
		(expectedHoldId !== undefined && hold.holdId !== expectedHoldId) ||
		hold.startGraceTimer === undefined
	) {
		return;
	}
	clearTimeout(hold.startGraceTimer);
	hold.startGraceTimer = undefined;
};

/** Whether the hold is known to have seen its conversation generating. */
export const hasRemixHandoffHoldStartedGenerating = (
	conversationId: string,
	holdId: number,
): boolean => {
	const hold = holds.get(conversationId);
	return hold?.holdId === holdId && hold.startGraceTimer === undefined;
};

/** Non-hook read: whether any handed-off generation is still being held. */
export const hasRemixHandoffHold = (): boolean => holds.size > 0;

/** Non-hook read of the held conversation ids. */
export const remixHandoffHoldConversationIdsNow = (): string[] => Array.from(holds.keys());

const getHandoffHoldsSnapshot = (): string =>
	JSON.stringify(Array.from(holds, ([conversationId, hold]) => [conversationId, hold.holdId]));

/** The held conversation ids and hold ids. Re-renders when a hold is added or released. */
export const useRemixHandoffHolds = (): { conversationId: string; holdId: number }[] => {
	// A string gives `useSyncExternalStore` a referentially stable primitive snapshot.
	const snapshot = useSyncExternalStore(
		subscribeToAnyMachine,
		getHandoffHoldsSnapshot,
		getHandoffHoldsSnapshot,
	);
	return (JSON.parse(snapshot) as [string, number][]).map(([conversationId, holdId]) => ({
		conversationId,
		holdId,
	}));
};

/** Test-only: drop every hold and its timers without notifying or reporting. */
export const __resetRemixHandoffHolds = (): void => {
	holds.forEach(clearHoldTimers);
	holds.clear();
};
